import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { api } from '../lib/api';
import { money, serviceDuration, shortDate, todayInput } from '../lib/format';
import { fieldErrors } from '../lib/form-errors';
import { haptic, isInsideTelegram, telegram } from '../lib/telegram';
import type { PublicArtist, PublicCalendarPeriod, PublicService, ShowcasedWork } from '../types';
import { ZoomableImage } from '../components/zoomable-image';
import { BackIcon } from '../components/icons';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  LoadingScreen,
  StatusBadge,
} from '../components/ui';

/** Русские названия полей публичной формы — для подписи ошибки. */
const APPLICATION_FIELD_LABELS: Record<string, string> = {
  clientName: 'Ваше имя',
  clientUsername: 'Ник в Telegram',
  title: 'Что нужно нарисовать',
  description: 'Описание',
  budget: 'Бюджет',
  desiredDeadline: 'Желаемый срок',
};

interface Receipt {
  publicNumber: string;
  title: string;
}

export function PublicArtistPage() {
  const { slug = '' } = useParams<{ slug: string }>();

  // Страница живёт вне рабочей области, поэтому кнопку «назад» она включает
  // сама — иначе из поиска художников выйти можно только закрыв Mini App.
  useEffect(() => {
    const back = telegram()?.BackButton;
    if (!back || history.length <= 1) return;

    const goBack = () => history.back();
    back.show();
    back.onClick(goBack);

    return () => {
      back.offClick(goBack);
      back.hide();
    };
  }, []);

  const query = useQuery({
    queryKey: ['public-artist', slug],
    queryFn: () =>
      api<{ artist: PublicArtist; services: PublicService[] }>(`/public/artists/${slug}`),
    retry: false,
  });

  if (query.isPending) return <LoadingScreen label="Открываем страницу художника" />;
  if (query.error) {
    return (
      <main className="public-page public-page--center">
        <ErrorNotice error={query.error} />
      </main>
    );
  }

  return <PublicArtistContent artist={query.data.artist} services={query.data.services} />;
}

function PublicArtistContent({
  artist,
  services,
}: {
  artist: PublicArtist;
  services: PublicService[];
}) {
  const [serviceId, setServiceId] = useState(services[0]?.id ?? '');
  const [clientName, setClientName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  const [deadline, setDeadline] = useState('');
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [allWorks, setAllWorks] = useState(false);
  const [, setLocation] = useLocation();
  const works = useQuery({
    queryKey: ['public-artist', artist.slug, 'works'],
    queryFn: () => api<{ items: ShowcasedWork[] }>(`/public/artists/${artist.slug}/works`),
    retry: false,
  });
  const calendar = useQuery({
    queryKey: ['public-artist', artist.slug, 'calendar'],
    queryFn: () =>
      api<{ items: PublicCalendarPeriod[] }>(`/public/artists/${artist.slug}/calendar`),
    enabled: artist.calendarVisible,
    retry: false,
  });
  const mutation = useMutation({
    mutationFn: () =>
      api<Receipt>(`/public/artists/${artist.slug}/applications`, {
        method: 'POST',
        auth: false,
        body: {
          ...(telegram()?.initData ? { initData: telegram()!.initData } : {}),
          clientName,
          ...(serviceId ? { serviceId } : {}),
          title,
          description,
          ...(budget ? { budget } : {}),
          ...(deadline ? { desiredDeadline: deadline } : {}),
          acceptTerms: true,
        },
      }),
    onSuccess: (created) => {
      haptic('success');
      setReceipt(created);
    },
  });
  const gallery = (works.data?.items ?? []).filter((work) => work.previewUrl !== null);
  // Шесть плиток закрывают экран; остальное разворачивается по «+N».
  const shownWorks = allWorks ? gallery : gallery.slice(0, 6);
  const hiddenWorks = gallery.length - shownWorks.length;
  const freeFrom = artist.calendarVisible ? firstFreeDay(calendar.data?.items ?? []) : null;
  const errors = fieldErrors(mutation.error);
  const belowMinimum = isBelowMinimum(artist.minOrderAmount, budget);

  if (receipt) {
    return (
      <main className="public-page public-page--center">
        <div className="success-panel__icon">✓</div>
        <p className="eyebrow">Заявка отправлена</p>
        <h1>{receipt.title}</h1>
        <p className="muted">
          Номер {receipt.publicNumber} — по нему заявку легко найти. Художник уже получил
          уведомление и скоро ответит.
        </p>
        <div className="stack">
          <Button wide onClick={() => setLocation('/my/artists')}>
            Смотреть другие услуги
          </Button>
          <Button variant="secondary" wide onClick={() => setReceipt(null)}>
            Вернуться к художнику
          </Button>
          {/*
            `close()` закрывает только Mini App. По прямой ссылке страница
            открывается во встроенном браузере, где кнопка молча ничего не
            делала — поэтому показываем её лишь там, где она работает.
          */}
          {isInsideTelegram() && (
            <Button variant="ghost" wide onClick={() => telegram()?.close()}>
              Закрыть приложение
            </Button>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="public-page">
      <header className={`public-hero ${artist.bannerUrl ? 'has-banner' : ''}`}>
        {artist.bannerUrl && <img className="public-hero__banner" src={artist.bannerUrl} alt="" />}
        <div className="public-hero__top">
          {window.history.length > 1 && (
            <button
              type="button"
              className="icon-button icon-button--back"
              aria-label="Назад"
              onClick={() => window.history.back()}
            >
              <BackIcon />
            </button>
          )}
          <div className="brand-lockup brand-lockup--light">
            <span className="brand-lockup__mark">AF</span>
            <span>Art Flow</span>
          </div>
        </div>
        <div className="profile-avatar profile-avatar--large">
          {artist.avatarUrl ? <img src={artist.avatarUrl} alt="" /> : artist.displayName[0]}
        </div>
        <h1>{artist.displayName}</h1>
        {/*
          Доступность стоит вплотную к имени: это первое, что решает заказчик —
          возьмётся ли этот художник и когда. Ниже уже описание и работы.
        */}
        <p className="public-hero__availability">
          <span className={artist.acceptsApplications ? 'is-open' : 'is-closed'}>
            {artist.commissionsStatus === 'OPEN'
              ? 'Принимаю заказы'
              : artist.commissionsStatus === 'WAITLIST'
                ? 'Лист ожидания'
                : 'Приём закрыт'}
          </span>
          {freeFrom && <> · свободен с {shortDate(freeFrom)}</>}
        </p>
        <p>{artist.description || 'Авторские работы на заказ'}</p>
        {artist.tags.length > 0 && (
          <div className="tag-list tag-list--center" aria-label="Теги заказов художника">
            {artist.tags.slice(0, 8).map((tag) => (
              <span key={tag} className="tag-pill tag-pill--hero">
                #{tag}
              </span>
            ))}
          </div>
        )}
        {/* Почта появляется здесь только с согласия художника: по умолчанию она
            остаётся запасным контактом по заказу, а не публичным объявлением. */}
        {artist.contactEmail && (
          <div className="link-list">
            <a className="link-chip" href={`mailto:${artist.contactEmail}`}>
              {artist.contactEmail}
            </a>
          </div>
        )}
        {artist.links.length > 0 && (
          <div className="link-list">
            {artist.links.map((link) => (
              <a
                key={link.url}
                className="link-chip"
                href={link.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </header>

      <section className="public-content">
        {/*
          Работы — главное на странице, поэтому идут первыми и без подписей:
          заказчик судит по картинке, а название чужого заказа ему ничего не
          говорит. Подробности — по нажатию, в увеличенном виде.
        */}
        {shownWorks.length > 0 && (
          <div className="work-tiles">
            {shownWorks.map((work) => (
              <ZoomableImage
                key={work.id}
                className="work-tile"
                url={work.previewUrl!}
                alt={work.title}
              />
            ))}
            {hiddenWorks > 0 && (
              <button
                type="button"
                className="work-tile work-tile--more"
                onClick={() => setAllWorks(true)}
              >
                +{hiddenWorks}
              </button>
            )}
          </div>
        )}

        {artist.calendarVisible && (
          <>
            <div className="section-heading">
              <h2>Загрузка художника</h2>
            </div>
            <PublicCalendar
              periods={calendar.data?.items}
              pending={calendar.isPending}
              error={calendar.error}
            />
          </>
        )}

        <div className="section-heading">
          <h2>Услуги</h2>
        </div>
        {services.length === 0 && (
          <EmptyState
            title="Услуг пока нет"
            text="Художник пока не выложил услуги. Напишите ему — возможно, он возьмётся за вашу идею."
          />
        )}
        {/*
          Карточка — не кнопка целиком: примеры работ внутри сами открываются
          по нажатию, а вложенная кнопка в кнопке невалидна. Поэтому выбор
          услуги — отдельная кнопка внизу карточки.
        */}
        <div className="service-picker">
          {services.map((service) => {
            const images = [service.previewUrl, service.secondaryUrl].filter((url): url is string =>
              Boolean(url),
            );
            const selected = serviceId === service.id;

            return (
              <article
                key={service.id}
                className={`card service-choice ${selected ? 'is-selected' : ''}`}
              >
                {images.length > 0 && (
                  <div className="service-choice__gallery">
                    {images.map((url) => (
                      <ZoomableImage
                        key={url}
                        url={url}
                        alt={`Пример работы: ${service.title}`}
                        thumb={false}
                      />
                    ))}
                  </div>
                )}
                <div className="service-choice__body">
                  <h3>{service.title}</h3>
                  {service.description && <p>{service.description}</p>}
                  {service.tags.length > 0 && (
                    <div className="tag-list" aria-label="Теги услуги">
                      {service.tags.slice(0, 5).map((tag) => (
                        <span key={tag} className="tag-pill">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="service-choice__footer">
                  {/*
                    Цена и срок стоят рядом одной строкой: заказчик сравнивает
                    услуги именно по этой паре, а не по описанию.
                  */}
                  <span className="service-choice__terms">
                    <strong>
                      {service.basePrice
                        ? `от ${money(service.basePrice, service.currency)}`
                        : 'По договорённости'}
                    </strong>
                    {serviceDuration(service) && (
                      <span className="meta">{serviceDuration(service)}</span>
                    )}
                  </span>
                  <Button
                    variant={selected ? 'primary' : 'secondary'}
                    onClick={() => setServiceId(selected ? '' : service.id)}
                  >
                    {selected ? 'Выбрано' : 'Выбрать'}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>

        <div className="public-terms">
          <span>
            Предоплата {artist.defaultDepositPercent}/{100 - artist.defaultDepositPercent}
          </span>
          <span>Правок в счёт цены: {artist.defaultRevisionLimit}</span>
          {artist.minOrderAmount && (
            <span>Заказы от {money(artist.minOrderAmount, artist.currency)}</span>
          )}
          {artist.installmentsEnabled && (
            <span>Рассрочка: до {artist.installmentParts} частей</span>
          )}
          <span>Оплата: {artist.currencies.join(', ')}</span>
        </div>

        {artist.acceptsApplications && !isInsideTelegram() && (
          <>
            <div className="section-heading">
              <h2>Оставить заявку</h2>
            </div>
            {/*
              Из браузера заявку не принимаем: без подписанного initData заказ
              не привязывается к заказчику и никогда не появится у него ни в
              боте, ни в приложении. Поэтому уводим в Telegram.
            */}
            <Card className="stack">
              <p className="muted">
                Заявки принимаются через Telegram: тогда заказ появится у вас в приложении, вы
                будете видеть каждый этап работы, а художник сможет писать вам прямо по нему.
              </p>
              {artist.telegramAppUrl ? (
                <a className="button button--primary button--wide" href={artist.telegramAppUrl}>
                  Открыть в Telegram
                </a>
              ) : (
                <p className="muted">
                  Найдите художника в Telegram-боте Art Flow — оттуда заявка дойдёт до него вместе с
                  вашим контактом.
                </p>
              )}
            </Card>
          </>
        )}

        {artist.acceptsApplications && isInsideTelegram() && (
          <>
            <div className="section-heading">
              <h2>Оставить заявку</h2>
            </div>
            <form
              // Ошибки показывают сами поля: браузерная подсказка дёргала форму.
              noValidate
              className="card stack public-form"
              onSubmit={(event) => {
                event.preventDefault();
                mutation.mutate();
              }}
            >
              <Field label="Ваше имя" error={errors.clientName}>
                <Input
                  required
                  value={clientName}
                  onChange={(event) => setClientName(event.target.value)}
                  placeholder="Анна"
                />
              </Field>
              <Field label="Что нужно нарисовать" error={errors.title}>
                <Input
                  required
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Портрет кота"
                />
              </Field>
              <Field label="Описание" error={errors.description}>
                <textarea
                  required
                  className="input input--textarea"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Расскажите о персонаже, стиле и пожеланиях"
                />
              </Field>
              <div className="form-grid">
                <Field
                  label={`Бюджет, ${artist.currency}`}
                  hint={budgetHint(artist)}
                  error={belowMinimum ? minimumMessage(artist) : errors.budget}
                >
                  <Input
                    inputMode="decimal"
                    value={budget}
                    onChange={(event) => setBudget(event.target.value)}
                  />
                </Field>
                <Field label="Желаемый срок" error={errors.desiredDeadline}>
                  <Input
                    type="date"
                    min={todayInput()}
                    value={deadline}
                    onChange={(event) => setDeadline(event.target.value)}
                  />
                </Field>
              </div>
              {artist.termsText && (
                <p className="terms">Отправляя заявку, вы принимаете правила: {artist.termsText}</p>
              )}
              {mutation.error && (
                <ErrorNotice error={mutation.error} labels={APPLICATION_FIELD_LABELS} />
              )}
              <Button
                type="submit"
                wide
                disabled={
                  !clientName || !title || !description || belowMinimum || mutation.isPending
                }
              >
                {mutation.isPending ? 'Отправляем…' : 'Отправить заявку'}
              </Button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}

/**
 * Порог заказа проверяется и на сервере; здесь он нужен, чтобы заказчик увидел
 * ограничение до отправки, а не после отказа.
 */
function isBelowMinimum(minOrderAmount: string | null, budget: string): boolean {
  const parsed = Number(budget.trim().replace(',', '.'));
  if (!minOrderAmount || budget.trim() === '' || Number.isNaN(parsed)) return false;
  return parsed < Number(minOrderAmount);
}

function minimumMessage(artist: PublicArtist): string {
  return `Художник берётся за заказы от ${money(artist.minOrderAmount ?? '0', artist.currency)}`;
}

function budgetHint(artist: PublicArtist): string | undefined {
  const parts: string[] = [];
  if (artist.minOrderAmount) parts.push(`От ${money(artist.minOrderAmount, artist.currency)}`);
  if (artist.currencies.length > 1) {
    parts.push(`также принимает ${artist.currencies.slice(1).join(', ')}`);
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function PublicCalendar({
  periods,
  pending,
  error,
}: {
  periods?: PublicCalendarPeriod[];
  pending: boolean;
  error: unknown;
}) {
  if (pending) {
    return (
      <Card className="public-calendar-card">
        <p className="muted">Смотрим, чем художник занят…</p>
      </Card>
    );
  }

  if (error) return <ErrorNotice error={error} />;

  const freeFrom = firstFreeDay(periods ?? []);

  if (!periods?.length) {
    return (
      <Card className="public-calendar-card">
        <p>Сейчас художник свободен — самое время написать.</p>
      </Card>
    );
  }

  return (
    <Card className="public-calendar-card">
      <p className="muted public-calendar-card__note">
        Здесь отображается занятость художника заказами. Что именно он рисует, для кого и за сколько
        — остаётся между ним и заказчиком.
      </p>
      <div className="public-calendar-list">
        {periods.map((period, index) => (
          <div
            key={`${period.from}-${period.to ?? 'open'}-${index}`}
            className="public-calendar-period"
          >
            <StatusBadge
              tone={
                period.busy === 'timeOff'
                  ? 'warning'
                  : period.busy === 'inProgress'
                    ? 'accent'
                    : 'neutral'
              }
            >
              {period.busy === 'timeOff'
                ? 'Недоступен'
                : period.busy === 'inProgress'
                  ? 'В работе'
                  : 'В очереди'}
            </StatusBadge>
            <strong>
              {shortDate(period.from)} — {shortDate(period.to)}
            </strong>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * Ближайший свободный день по публичной занятости. Считается из тех же полос,
 * что видит заказчик: очередь и оценки художника наружу не отдаются, и это
 * правильно — публичной странице хватает «когда освободится».
 */
function firstFreeDay(periods: PublicCalendarPeriod[]): string | null {
  const today = new Date().toISOString().slice(0, 10);
  let free = today;

  // Полосы приходят отсортированными по началу: идём подряд и двигаем границу.
  for (const period of periods) {
    if (!period.to || period.to < free) continue;
    if (period.from > free) break;

    free = nextDay(period.to);
  }

  return free === today ? null : free;
}

function nextDay(day: string): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}
